/**
 * Event wrapper for `track` event.
 * Reference: developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/ontrack
 * @method ontrack
 * @param  {Function} listener The listener function for the event.
 * @return {Boolean}  Whether the assignment succeeded or not.
 */
export default function ontrack (listener) {
  const { nativePeer, trackManager, log } = this

  nativePeer.ontrack = event => {
    /**
     * transceiver: The RTCRtpTransceiver for this remote track. (Available in unified-plan)
     * receiver: The RTCRtpReceiver for this remote track.
     * track: The remote MediaStreamTrack.
     * streams: Array of MediaStreams the track is in.
     */
    // event object contains transceiver which already has track attached to its receiver
    const { track: nativeTrack, streams } = event
    log.debug(`Peer received ${nativeTrack.kind} Track ${nativeTrack.id}.`)

    /*
     * When the remote side adds a track, it should have an associated MediaStream
     *    which we get access to here. This allows us to listen for events on that
     *    MediaStream (important for knowing when the track has ended).
     * It's possible that the remote Sender does not have a Stream set, so we create
     *    a Stream for it. This should be considered a problematic scenario, since
     *    we rely on the remote Stream for certain events.
     * Reference: KAA-2628
     */
    let targetStream
    if (streams.length === 0) {
      targetStream = new MediaStream([nativeTrack])
      log.debug('New Track is not associated with remote Stream.')
    } else {
      targetStream = streams[0]
      log.debug(`New Track is associated with remote Stream ${targetStream.id}.`)
    }

    // Convert the native MediaStreamTrack into a Track object.
    // Specify that this is not a local one (i.e. it's a remote track)
    const track = trackManager.add(nativeTrack, targetStream, false)

    listener(track)
  }

  return true
}
